// Spec 45 component-behavior — cmp_ds_spinner.
//
// Indeterminate progress indicator. Two render modes per spec 43
// loading_state pattern AC: skeletons first, spinner only when
// skeletons would mislead. Sizes: sm (16px) / md (24px) / lg (32px).
//
// a11y: role='status' + aria-busy + aria-label. Caller-supplied label
// is REQUIRED so screenreaders announce something meaningful — no
// silent default like "Loading…" because spinners often appear in
// surfaces where a more specific announcement helps ("Saving payroll…",
// "Verifying signature…").

import { forwardRef, type HTMLAttributes } from "react";

import { cx } from "../lib/cx.ts";
import { isDevGuardActive } from "../lib/dev-guard.ts";

export type SpinnerSize = "sm" | "md" | "lg";

export interface SpinnerProps extends HTMLAttributes<HTMLDivElement> {
  readonly size?: SpinnerSize;
  /** Required accessible label — what is loading? */
  readonly "aria-label": string;
}

export const Spinner = forwardRef<HTMLDivElement, SpinnerProps>(
  ({ size = "md", "aria-label": ariaLabel, className, ...rest }, ref) => {
    if (isDevGuardActive() && (typeof ariaLabel !== "string" || ariaLabel.trim().length === 0)) {
      throw new Error(
        "<Spinner> requires a non-empty aria-label describing what is loading (e.g., 'Saving payroll').",
      );
    }
    return (
      <div
        ref={ref}
        role="status"
        aria-busy="true"
        aria-label={ariaLabel}
        data-component="spinner"
        data-size={size}
        className={cx("nai-spinner", className)}
        {...rest}
      />
    );
  },
);
Spinner.displayName = "Spinner";
